import {asInstanceOf, panic, UUID} from "@opendaw/lib-std"
import {AudioUnitBox, NoteClipBox, NoteEventBox, NoteEventCollectionBox, TrackBox} from "@opendaw/studio-boxes"
import {ColorCodes, InstrumentFactories, TrackType} from "@opendaw/studio-adapters"
import {Project} from "@opendaw/studio-core"
import {AcidKit} from "@/samples/AcidKit"
import {AcidTrackSpec} from "@/generate/AcidTrack"
import {DrumEvent, DrumGenerator, DrumOptions, DrumVariant} from "@/generate/DrumGenerator"
import {DrumLane} from "@/generate/DrumLane"

export type DrumClip = {
    kind: DrumVariant
    clip: NoteClipBox
    collection: NoteEventCollectionBox
}

type ClipSpec = {kind: DrumVariant, name: string}

// The plain pattern exists twice: handing over between two identical clips is what makes the engine report
// every cycle boundary, which is the arranger's clock.
const Clips: ReadonlyArray<ClipSpec> = [
    {kind: "pattern", name: "Pattern A"},
    {kind: "pattern", name: "Pattern B"},
    {kind: "fill", name: "Fill"},
    {kind: "drop", name: "Drop"},
    {kind: "build", name: "Build"}
]

export class DrumMachine {
    // Eight lanes hitting at once clip the master, and there is no limiter on the output yet.
    static readonly UnitVolume = -12.0

    static create(project: Project, spec: AcidTrackSpec): DrumMachine {
        const {api, editing, timelineBox, boxGraph} = project
        const {audioUnitBox, trackBox, clips} = editing.modify(() => {
            timelineBox.bpm.setValue(spec.bpm)
            // The arrangement rides the clip clock, so the 4-bar default loop only serves to re-anchor every
            // modulator to phase zero on each wrap, which freezes the 303 into one short repeating curve.
            timelineBox.loopArea.enabled.setValue(false)
            const {trackBox, audioUnitBox} = api.createInstrument(InstrumentFactories.Playfield, {
                name: "Drums",
                attachment: AcidKit.Pads.map(({note, sample, exclude}) => ({
                    note,
                    uuid: UUID.parse(sample.uuid),
                    name: sample.name,
                    durationInSeconds: sample.duration,
                    exclude
                }))
            })
            audioUnitBox.volume.setValue(DrumMachine.UnitVolume)
            // One four-bar clip per variant and no regions: the arranger launches them.
            const clips = Clips.map(({kind, name}, index): DrumClip => {
                const collection = NoteEventCollectionBox.create(boxGraph, UUID.generate())
                const clip = NoteClipBox.create(boxGraph, UUID.generate(), box => {
                    box.index.setValue(index)
                    box.label.setValue(name)
                    box.hue.setValue(ColorCodes.forTrackType(TrackType.Notes))
                    box.duration.setValue(DrumLane.CycleDuration)
                    box.triggerMode.loop.setValue(true)
                    box.clips.refer(trackBox.clips)
                    box.events.refer(collection.owners)
                })
                return {kind, clip, collection}
            })
            return {audioUnitBox, trackBox, clips}
        }).unwrap("drum-machine")
        const machine = new DrumMachine(project, audioUnitBox, trackBox, clips)
        // Written BEFORE the worklet starts so the notes travel in the engine's initial graph dump.
        machine.write(spec.lanes, spec.drums)
        return machine
    }

    readonly #project: Project
    readonly #audioUnitBox: AudioUnitBox
    readonly #trackBox: TrackBox
    readonly #clips: ReadonlyArray<DrumClip>

    constructor(project: Project, audioUnitBox: AudioUnitBox, trackBox: TrackBox, clips: ReadonlyArray<DrumClip>) {
        this.#project = project
        this.#audioUnitBox = audioUnitBox
        this.#trackBox = trackBox
        this.#clips = clips
    }

    get audioUnitBox(): AudioUnitBox {return this.#audioUnitBox}
    get trackBox(): TrackBox {return this.#trackBox}
    get clips(): ReadonlyArray<DrumClip> {return this.#clips}

    // A clip of that kind other than `playing`, so a repeat of the same kind still changes clips.
    clipOf(kind: DrumVariant, playing: UUID.Bytes): DrumClip {
        return this.#clips.find(entry => entry.kind === kind && !UUID.equals(entry.clip.address.uuid, playing))
            ?? panic(`No spare '${kind}' clip`)
    }

    write(lanes: ReadonlyArray<DrumLane>, options: DrumOptions): void {
        this.#project.editing.modify(() => this.#clips.forEach(({kind, collection}) =>
            this.#replace(collection, DrumGenerator.variant(kind, lanes, options))))
    }

    #replace(collection: NoteEventCollectionBox, events: ReadonlyArray<DrumEvent>): void {
        const {boxGraph} = this.#project
        collection.events.pointerHub.incoming()
            .map(({box}) => asInstanceOf(box, NoteEventBox))
            .forEach(box => box.delete())
        events.forEach(({position, duration, pitch, velocity}) =>
            NoteEventBox.create(boxGraph, UUID.generate(), box => {
                box.position.setValue(position)
                box.duration.setValue(duration)
                box.pitch.setValue(pitch)
                box.velocity.setValue(velocity)
                box.events.refer(collection.events)
            }))
    }
}
